import {
  Breadcrumbs,
  CircularProgress,
  Grid,
  Paper,
  TextField,
  Typography,
} from "@material-ui/core";
import Link from "@material-ui/core/Link";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import SortableTable from "../../../Components/SortableTable";
import Title from "../../../Components/Title";
import {
  ECABIN,
  formatTimeWithTZOffset,
  getLCLocation,
  getLatestLifeCycleState,
  getToken,
  requestErrorHandler,
  tableAlternateColors,
} from "../../../Utils/Common";
import EnvSettings from "../../../Utils/EnvSettings";
import { useStyles } from "../../../Utils/Styles";
import SubAssemblyDialog from "./SubAssemblyDialog";

const server = EnvSettings.server;

export default function SerialisedSubAssemblies() {
  const classes = useStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [serialisedSubAssemblies, setSerialisedSubAssemblies] = useState([]);
  const [filteredSerialisedSubAssemblies, setFilteredSerialisedSubAssemblies] =
    useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [selectedSubAssembly, setSelectedSubAssembly] = useState(null);
  const [selectedSubAssemblyId, setSelectedSubAssemblyId] = useState(null);

  const fetchSerialisedSubAssemblies = useCallback(() => {
    setIsLoading(true);
    setErrorMessage("");
    fetch(server + "/get_serialised_subassemblies", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        token: getToken(),
      }),
    })
      .then((res) => res.json())
      .then((result) => {
        requestErrorHandler(result, dispatch);
        if (result.error) {
          setSerialisedSubAssemblies([]);
          setErrorMessage(
            result.message ||
              t(
                "manufacturing.serialisedSubAssembliesFetchFailed",
                "Failed to fetch serialised sub-assemblies."
              )
          );
        } else {
          setSerialisedSubAssemblies(
            Array.isArray(result.serialisedSubAssemblies)
              ? result.serialisedSubAssemblies
              : []
          );
          setErrorMessage("");
        }
      })
      .catch(() => {
        setSerialisedSubAssemblies([]);
        setErrorMessage(
          t(
            "manufacturing.serialisedSubAssembliesFetchFailed",
            "Failed to fetch serialised sub-assemblies."
          )
        );
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [dispatch, t]);

  useEffect(() => {
    fetchSerialisedSubAssemblies();
  }, [fetchSerialisedSubAssemblies]);

  const processedAssemblies = useMemo(() => {
    return serialisedSubAssemblies.map((assembly) => {
      const deviceState = getLatestLifeCycleState(
        assembly.Device?.LifeCycleStates
      );
      const ownState = getLatestLifeCycleState(assembly.LifeCycleStates);
      const resolvedState = deviceState || ownState;
      return {
        ...assembly,
        currentLocation: resolvedState?.location
          ? getLCLocation(resolvedState.location)
          : null,
      };
    });
  }, [serialisedSubAssemblies]);

  useEffect(() => {
    if (!selectedSubAssemblyId) {
      return;
    }
    const updatedAssembly = processedAssemblies.find(
      (assembly) => assembly.id === selectedSubAssemblyId
    );
    if (updatedAssembly) {
      setSelectedSubAssembly(updatedAssembly);
    } else {
      setSelectedSubAssembly(null);
      setSelectedSubAssemblyId(null);
    }
  }, [processedAssemblies, selectedSubAssemblyId]);

  useEffect(() => {
    const term = searchValue.trim().toLowerCase();
    if (!term) {
      setFilteredSerialisedSubAssemblies(processedAssemblies);
      return;
    }
    const filtered = processedAssemblies.filter((assembly) => {
      const fields = [
        assembly.number,
        assembly.model,
        assembly.color,
        assembly.ManufacturingEntry?.uuid,
        assembly.ManufacturingEntry?.batchNumber,
        assembly.ManufacturingEntry?.StockItem?.sku,
        assembly.ManufacturingEntry?.StockItem?.name,
        assembly.ManufacturingEntry?.StockChange?.Warehouse?.name,
        assembly.Device?.serialNumber,
        assembly.currentLocation,
      ];
      return fields.some((field) => {
        if (field === undefined || field === null) {
          return false;
        }
        return field.toString().toLowerCase().includes(term);
      });
    });
    setFilteredSerialisedSubAssemblies(filtered);
  }, [processedAssemblies, searchValue]);

  const headCells = useMemo(
    () => [
      {
        id: "ManufacturingEntry.createdAt",
        label: t("manufacturing.manufacturedAt", "Manufactured at"),
        disablePadding: false,
        numeric: false,
      },
      {
        id: "number",
        label: t("serialisedSubAssembly.number", "Chassis number"),
        disablePadding: false,
        numeric: false,
      },
      {
        id: "model",
        label: t("serialisedSubAssembly.model", "Model"),
        disablePadding: false,
        numeric: false,
      },
      {
        id: "color",
        label: t("serialisedSubAssembly.color", "Color"),
        disablePadding: false,
        numeric: false,
      },
      {
        id: "ManufacturingEntry.StockItem.sku",
        label: t(
          "manufacturing.resultingStockItem",
          "Resulting stock item (SKU)"
        ),
        disablePadding: false,
        numeric: false,
      },
      {
        id: "ManufacturingEntry.batchNumber",
        label: t("manufacturing.batchNumber", "Batch number"),
        disablePadding: false,
        numeric: false,
      },

      {
        id: "Device.serialNumber",
        label: t("devices.serialNumber", "Assigned device"),
        disablePadding: false,
        numeric: false,
      },
      {
        id: "currentLocation",
        label: t("location", "Location"),
        disablePadding: false,
        numeric: false,
      },
    ],
    [t]
  );

  const specialFormatting = useMemo(
    () => [
      {
        id: "ManufacturingEntry.createdAt",
        format: (data) => (data ? formatTimeWithTZOffset(data, ECABIN) : ""),
      },
    ],
    [t]
  );

  return (
    <Grid container spacing={3}>
      <SubAssemblyDialog
        subAssembly={selectedSubAssembly}
        onClose={() => {
          setSelectedSubAssembly(null);
          setSelectedSubAssemblyId(null);
        }}
        onRefresh={fetchSerialisedSubAssemblies}
      />
      <Grid item xs={12}>
        <Breadcrumbs aria-label="breadcrumb">
          <Link color="inherit" href="/cpanel/manufacturing">
            {t("manufacturing.header", "Manufacturing")}
          </Link>
          <Typography color="textPrimary">
            {t(
              "manufacturing.serialisedSubAssemblies",
              "Serialised sub-assemblies"
            )}
          </Typography>
        </Breadcrumbs>
      </Grid>
      <Grid item xs={12}>
        <Paper className={classes.paper}>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12}>
              <Title>
                {t(
                  "manufacturing.serialisedSubAssemblies",
                  "Serialised sub-assemblies"
                )}
              </Title>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                variant="outlined"
                label={t("common.search", "Search")}
                value={searchValue}
                onChange={(event) => setSearchValue(event.target.value)}
              />
            </Grid>
          </Grid>
          {errorMessage ? (
            <Grid container style={{ marginTop: 16 }}>
              <Grid item xs={12}>
                <Paper
                  elevation={0}
                  style={{ padding: 16, background: "#ffeeee" }}
                >
                  {errorMessage}
                </Paper>
              </Grid>
            </Grid>
          ) : null}
        </Paper>
      </Grid>
      <Grid item xs={12}>
        <Paper className={classes.paper}>
          {isLoading ? (
            <CircularProgress />
          ) : filteredSerialisedSubAssemblies.length ? (
            <SortableTable
              tableLayout="fixed"
              downloadButton
              header={t(
                "manufacturing.serialisedSubAssemblies",
                "Serialised sub-assemblies"
              )}
              headCells={headCells}
              tableData={filteredSerialisedSubAssemblies}
              rowsPerPage={25}
              defaultSortRow="ManufacturingEntry.createdAt"
              alternateColors
              getRowStyle={tableAlternateColors}
              specialDataFormatting={specialFormatting}
              handleRowClick={(subAssembly) => {
                setSelectedSubAssembly(subAssembly);
                setSelectedSubAssemblyId(subAssembly?.id ?? null);
              }}
            />
          ) : (
            <Typography variant="body2" color="textSecondary" align="center">
              {searchValue
                ? t(
                    "manufacturing.noSerialisedSubAssembliesMatchSearch",
                    "No sub-assemblies match your search."
                  )
                : t(
                    "manufacturing.noSerialisedSubAssembliesFound",
                    "No serialised sub-assemblies available."
                  )}
            </Typography>
          )}
        </Paper>
      </Grid>
    </Grid>
  );
}
